// Farb-Logik der Pipeline-Phasen und Charts (Design 3.0, Etappe D2).
//
// Chart-Regel des Konzepts: max. 2–3 Farbfamilien je Chart — und jede
// Familie ist eine Ramp (Stufen einer Farbe), keine Einzelfarbe:
//   · ramp  (Indigo) → CRM-Objekte: Leads, Unternehmen
//   · ramp2 (Blau)   → Projekte & Zeit
//   · Grau           → „Verloren“/Restmengen
// So unterscheiden sich Kacheln sichtbar (Lead-Donut ≠ Projekt-Donut),
// bleiben aber im System. Braucht ein Chart viele Reihen, kombiniert es
// Stufen beider Ramps (chartSeriesColor) — wie die Referenz-Screenshots.
//
// Status-Chips behalten zwei semantische Ausnahmen: die letzte Phase
// (gewonnen/erfüllt/abgeschlossen) ist grün, der Verlust-Zweig rot —
// dazwischen die Ramp der jeweiligen Familie in lesbaren Stufen.
//
// Die Phasenlisten kommen aus der Pipeline-Konfiguration (B3), die
// Zuordnung läuft daher über die Position, nicht über feste Keys.

// Charts: helle Einstiege sind erlaubt (Flächenfarben)
const CHART_STEPS = {
  1: [500],
  2: [300, 700],
  3: [200, 500, 700],
  4: [200, 300, 500, 700],
  5: [200, 300, 500, 700, 900],
};

// Chips: Farbe ist zugleich Textfarbe (alpha()-Soft-Chip) → dunklere Stufen
const CHIP_STEPS = {
  1: [700],
  2: [500, 700],
  3: [500, 700, 900],
  4: [300, 500, 700, 900],
  5: [300, 500, 700, 900, 900],
};

const stepFor = (steps, count, index) => {
  const row = steps[Math.min(Math.max(count, 1), 5)];
  return row[Math.min(index, row.length - 1)];
};

// Vier Familien, zwei harmonisierte Paare (Alexanders Regel — pro Chart
// nur ein Paar): Paar A 'primary' (Indigo) + 'support' (Blau);
// Paar B 'teal' + 'green' (Zeit, künftig Umsatz/Angebote).
const rampOf = (theme, family) => ({
  primary: theme.palette.tokens.ramp,
  support: theme.palette.tokens.ramp2,
  teal:    theme.palette.tokens.ramp3,
  green:   theme.palette.tokens.ramp4,
}[family] || theme.palette.tokens.ramp);

// key → Farbe für Charts (Donut, gestapelte Balken): reine Ramp + Grau.
export function phaseChartColors(theme, phases, lost, family = 'primary') {
  const ramp = rampOf(theme, family);
  const map = {};
  phases.forEach((p, i) => { map[p.key] = ramp[stepFor(CHART_STEPS, phases.length, i)]; });
  if (lost) map[lost.key] = theme.palette.tokens.chartNeutral;
  return map;
}

// key → Farbe für Status-Chips: Ramp, letzte Phase grün, Verlust rot.
export function phaseChipColors(theme, phases, lost, family = 'primary') {
  const ramp = rampOf(theme, family);
  const map = {};
  phases.forEach((p, i) => { map[p.key] = ramp[stepFor(CHIP_STEPS, phases.length, i)]; });
  if (phases.length) map[phases[phases.length - 1].key] = theme.palette.success.main;
  if (lost) map[lost.key] = theme.palette.error.main;
  return map;
}

// Einzelne Ramp-Stufe für Flächen (z. B. Phasenband-Pillen).
export function phaseRampColor(theme, index, count, family = 'primary') {
  return rampOf(theme, family)[stepFor(CHART_STEPS, count, index)];
}

// Reihenfarbe für Charts mit mehreren gleichrangigen Reihen: wechselt
// zwischen den beiden Familien EINES Paares (Indigo 500, Blau 500,
// Indigo 300, …) — viele Stufen, kein Kirmesrad. pair: 'cool' | 'fresh'.
const PAIRS = { cool: ['primary', 'support'], fresh: ['teal', 'green'] };
const SERIES_STEPS = [500, 300, 700];
export function chartSeriesColor(theme, index, pair = 'cool') {
  const fams = PAIRS[pair] || PAIRS.cool;
  const family = fams[index % 2];
  const step = SERIES_STEPS[Math.floor(index / 2) % SERIES_STEPS.length];
  return rampOf(theme, family)[step];
}
